"use client";

import { cn } from "@/lib/utils";
import { createClient } from "@/lib/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";

export function LoginForm({
  className,
  ...props
}: React.ComponentPropsWithoutRef<"div">) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const supabase = createClient();
    setIsLoading(true);
    setError(null);

    try {
      // First try to authenticate as owner (Supabase Auth user)
      const { error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (!authError) {
        // Successfully authenticated as owner
        router.push("/dashboard/messenger-live");
        return;
      }

      // If Supabase Auth failed, try to authenticate as employee via server API
      const checkResponse = await fetch("/api/auth/employee-check", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!checkResponse.ok) {
        throw new Error("Неверные данные для входа");
      }

      const checkData = await checkResponse.json();

      if (!checkData.success) {
        throw new Error("Неверные данные для входа");
      }

      // Phase A login-cutover: если сервер синхронизировал пароль в auth.users (checkData.authSynced),
      // ДОПОЛНИТЕЛЬНО входим настоящей Supabase-сессией — она нужна сотруднику для realtime
      // (broadcast inbox). proxy.ts НЕ считает это leak'ом: JWT несёт app_metadata.role=employee.
      // Если синк/вход не прошёл — не страшно: ниже всё равно ставится employee_session cookie,
      // приложение работает (без живого realtime, только polling).
      if (checkData.authSynced) {
        await supabase.auth
          .signInWithPassword({ email, password })
          .catch(() => {});
      }

      // employee_session cookie ставим ВСЕГДА (совместимость со всем, что её читает: /api/auth/me,
      // messenger/init и т.п.). Это гарантированный путь авторизации; realtime даёт сессия выше.
      const sessionResponse = await fetch("/api/auth/employee-login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ employeeId: checkData.employee.id }),
      });

      if (!sessionResponse.ok) {
        throw new Error("Не удалось создать сессию сотрудника");
      }

      router.push("/dashboard/messenger-live");
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : "Произошла ошибка");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Вход</CardTitle>
          <CardDescription>
            Введите email для входа в аккаунт
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin}>
            <div className="flex flex-col gap-6">
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="m@example.com"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <div className="flex items-center">
                  <Label htmlFor="password">Пароль</Label>
                  <Link
                    href="/auth/forgot-password"
                    className="ml-auto inline-block text-sm underline-offset-4 hover:underline"
                  >
                    Забыли пароль?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              {error && <p className="text-sm text-red-500">{error}</p>}
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "Вход..." : "Войти"}
              </Button>
            </div>
            <div className="mt-4 text-center text-sm">
              Нет аккаунта?{" "}
              <Link
                href="/auth/sign-up"
                className="underline underline-offset-4"
              >
                Зарегистрироваться
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
